import React, { useState } from "react";
import PropTypes from "prop-types";
import { Button } from "components/core/Button";
import { Image } from "@sparefoot/react-commons";
import MapPlaceholder from "assets/images/Map.svg";
import GroupIcon from "assets/images/Group.svg";
import { MISSING_FACILITY_IMAGE_URL } from "store/selectors/FacilityPage";
import MapView from "components/organisms/MapView/MapView";
import FacilityGalleryModal from "./FacilityGalleryModal";

import "./FacilityGallery.scss";

const FacilityGallery = ({
	imagesProps,
	facilityName,
	staticMapUrl,
	facility,
	currentFacilityLowestPrice,
	currentFacilityStartingPrice,
	facilityCards
}) => {
	const { images = [] } = imagesProps || {};
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isMapOpen, setIsMapOpen] = useState(false);
	const [mapRetryCount, setMapRetryCount] = useState(0);
	const MAX_MAP_RETRIES = 3;

	const handleMapImageError = () => {
		if (mapRetryCount < MAX_MAP_RETRIES) {
			setMapRetryCount(c => c + 1);
		}
	};

	// Get the main image or use placeholder
	const mainImage = images[0] || MISSING_FACILITY_IMAGE_URL;
	const isPlaceholderImage = mainImage === MISSING_FACILITY_IMAGE_URL;

	// Total number of photos for the button label
	const totalPhotos = images.length;

	const handleOpenModal = () => {
		setIsModalOpen(true);
	};

	const handleCloseModal = () => {
		setIsModalOpen(false);
	};

	const handleOpenMap = () => {
		setIsMapOpen(true);
	};

	const handleCloseMap = () => {
		setIsMapOpen(false);
	};
	const mapFacilityCards = React.useMemo(() => {
		const cardsFacilities  = facilityCards?.facilities ?? [];

		if (!facility) return facilityCards;

		const formattedFacility = {
			...facility,
			lowestPrice: currentFacilityLowestPrice,
			startingPrice: currentFacilityStartingPrice,
			id: facility?.location?.id ?? 1,
			image: imagesProps?.images?.[0],
			distance: facility.facilityDistanceCity || 0.1,
			schema: {
				location: {
					id: facility?.location?.id ?? 1,
					lowestPrice: currentFacilityLowestPrice,
					latitude: facility?.location?.latitude,
					longitude: facility?.location?.longitude,
					url: facility?.location?.url
				},
				reviews: {
					average: facility?.reviews?.average ?? 0,
					number: facility?.reviews?.number ?? 0
				},
				hasOnlineMoveins: facility?.hasOnlineMoveins ?? false
			},
			reviews: {
				average: facility?.reviews?.average ?? 0,
				number: facility?.reviews?.number ?? 0
			}
		};
		const exists = cardsFacilities .some((f) => f.id === formattedFacility.id);

		return {
			...facilityCards,
			facilities: exists ? cardsFacilities  : [formattedFacility, ...cardsFacilities ]
		};
	}, [facility, facilityCards, currentFacilityLowestPrice, currentFacilityStartingPrice, imagesProps]);

	return (
		<div className="facility-gallery">
			{/* Main Image */}
			<div className="facility-gallery__main">
				<Image
					src={mainImage}
					alt="Facility main view"
					className="facility-gallery__image facility-gallery__image--main"
					fetchpriority="high"
				/>
				{isPlaceholderImage && (
					<span className="facility-gallery__placeholder-text">
						Image Coming Soon
					</span>
				)}
				{!isPlaceholderImage && totalPhotos > 1 && (
					<Button
						className="facility-gallery__view-all-btn"
						onClick={handleOpenModal}
						type="button"
						fullWidth={false}
					>
						<Image
							src={GroupIcon}
							alt=""
							className="facility-gallery__btn-icon"
						/>
						+{totalPhotos - 1}{" "}
						{totalPhotos - 1 === 1 ? "Photo" : "Photos"}
					</Button>
				)}
			</div>

			{/* Map Image */}
			<div className="facility-gallery__map">
				{staticMapUrl ? (
					<Button
						className="facility-gallery__map-btn"
						onClick={handleOpenMap}
						type="button"
					>
						<Image
							key={mapRetryCount}
							src={staticMapUrl}
							alt="Facility location map"
							className="facility-gallery__image facility-gallery__image--map"
							onError={handleMapImageError}
							referrerPolicy="same-origin"
						/>
					</Button>
				) : (
					<Button
						className="facility-gallery__map-btn"
						onClick={handleOpenMap}
						type="button"
					>
						<Image
							src={MapPlaceholder}
							alt="Map placeholder"
							className="facility-gallery__image facility-gallery__image--map"
						/>
					</Button>
				)}
			</div>
			<MapView
				isOpen={isMapOpen}
				handleClose={handleCloseMap}
				facilityCards={mapFacilityCards}
				facility={mapFacilityCards?.facilities?.[0]}
			/>
			{/* Gallery Modal */}
			<FacilityGalleryModal
				isOpen={isModalOpen}
				onClose={handleCloseModal}
				images={images}
				facilityName={facilityName}
			/>
		</div>
	);
};

FacilityGallery.propTypes = {
  imagesProps: PropTypes.shape({
    images: PropTypes.arrayOf(PropTypes.string)
  }),

  facilityName: PropTypes.string,

  staticMapUrl: PropTypes.string,

  currentFacilityLowestPrice: PropTypes.number,

  currentFacilityStartingPrice: PropTypes.number,

  facility: PropTypes.shape({
	facilityDistanceCity: PropTypes.number,
	hasOnlineMoveins: PropTypes.bool,
    location: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      latitude: PropTypes.number,
      longitude: PropTypes.number,
      url: PropTypes.string
    }),
    reviews: PropTypes.shape({
      average: PropTypes.number,
      number: PropTypes.number
    })
  }),

  facilityCards: PropTypes.shape({
    facilities: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        lowestPrice: PropTypes.number,
        image: PropTypes.string,
        schema: PropTypes.shape({
          location: PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            latitude: PropTypes.number,
            longitude: PropTypes.number,
            url: PropTypes.string
          }),
          reviews: PropTypes.shape({
            average: PropTypes.number,
            number: PropTypes.number
          })
        }),
        reviews: PropTypes.shape({
          average: PropTypes.number,
          number: PropTypes.number
        })
      })
    )
  })
};
FacilityGallery.defaultProps = {
	imagesProps: { images: [] },
	facilityName: "Facility",
	staticMapUrl: ""
};

export default FacilityGallery;
